/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

@file:Suppress("DuplicatedCode", "ObjectPropertyName", "Detekt:TooManyFunctions", "ClassName")
@file:KlangScript.Library("sprudel")

package io.peekandpoke.klang.sprudel.lang

import io.peekandpoke.klang.audio_bridge.constants.COMPRESSOR_ATTACK_SECONDS
import io.peekandpoke.klang.audio_bridge.constants.COMPRESSOR_KNEE_DB
import io.peekandpoke.klang.audio_bridge.constants.COMPRESSOR_RATIO
import io.peekandpoke.klang.audio_bridge.constants.COMPRESSOR_RELEASE_SECONDS
import io.peekandpoke.klang.audio_bridge.constants.COMPRESSOR_THRESHOLD_DB
import io.peekandpoke.klang.script.annotations.KlangScript
import io.peekandpoke.klang.script.ast.CallInfo
import io.peekandpoke.klang.sprudel.SprudelPattern
import io.peekandpoke.klang.sprudel.SprudelVoiceData
import io.peekandpoke.klang.sprudel._liftOrReinterpretNumericalField
import io.peekandpoke.klang.sprudel._mapNumericField
import io.peekandpoke.klang.sprudel.katalystParamsOrNew
import io.peekandpoke.klang.sprudel.lang.SprudelDslArg.Companion.asSprudelDslArgs

// -- compressor ------------------------------------------------------------------------------------------------------

// Each setter writes the orbit chain's matching slot (`compressor.threshold`, ...), which is what
// makes this door an alias of `katp` (signal-flow plan §7, Katalyst step 5a), and then fills the other
// four. The slots are the compressor's only storage since Katalyst step 5b-3.

/**
 * Fills the compressor stage's companions from `constants/BusEffectDefaults.kt` (`/dsl-design` §4 is
 * the rule; this is only what THIS door does). Called from every one of the five setters, because
 * the compressor has no name knob: `compressor(ratio = 8)` compresses at the constant threshold.
 *
 * Since step 5b-1 the orbit's compressor reads the SLOTS and nothing else, and since step 5b-3
 * there are no voice fields beside them.
 *
 * Byte-identical to the engine's own fallback, which is what let the fill move to the door: the
 * five constants written here are exactly what the engine substitutes for an unset slot
 * (`KatalystCompressorWriter`). Until step 5a-3 this door filled nothing and the asymmetry with `reverb(...)` was recorded
 * rather than fixed.
 *
 * The HEAD setter, the threshold, is the one setter here a bare call can reach with a null; it then
 * writes nothing at all (`if (value != null)`).
 */
private fun SprudelVoiceData.fillCompressorDefaults() {
    val slots = katalystParamsOrNew()

    slots.setOrDefault("compressor.threshold", value = null, default = COMPRESSOR_THRESHOLD_DB)
    slots.setOrDefault("compressor.ratio", value = null, default = COMPRESSOR_RATIO)
    slots.setOrDefault("compressor.knee", value = null, default = COMPRESSOR_KNEE_DB)
    slots.setOrDefault("compressor.attack", value = null, default = COMPRESSOR_ATTACK_SECONDS)
    slots.setOrDefault("compressor.release", value = null, default = COMPRESSOR_RELEASE_SECONDS)
}

private val compressorThresholdMutation = voiceSetter { raw ->
    val value = raw?.asDoubleOrNull()

    if (value != null) {
        katalystParamsOrNew().set("compressor.threshold", value)
        fillCompressorDefaults()
    }
}

private fun applyCompressorThreshold(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.katalystParams?.get("compressor.threshold") }, update = compressorThresholdMutation)
    }

    return source._liftOrReinterpretNumericalField(args, compressorThresholdMutation)
}

private val compressorRatioMutation = voiceSetter { raw ->
    val value = raw?.asDoubleOrNull()

    if (value != null) {
        katalystParamsOrNew().set("compressor.ratio", value)
        fillCompressorDefaults()
    }
}

private fun applyCompressorRatio(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.katalystParams?.get("compressor.ratio") }, update = compressorRatioMutation)
    }

    return source._liftOrReinterpretNumericalField(args, compressorRatioMutation)
}

private val compressorKneeMutation = voiceSetter { raw ->
    val value = raw?.asDoubleOrNull()

    if (value != null) {
        katalystParamsOrNew().set("compressor.knee", value)
        fillCompressorDefaults()
    }
}

private fun applyCompressorKnee(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.katalystParams?.get("compressor.knee") }, update = compressorKneeMutation)
    }

    return source._liftOrReinterpretNumericalField(args, compressorKneeMutation)
}

private val compressorAttackMutation = voiceSetter { raw ->
    val value = raw?.asDoubleOrNull()

    if (value != null) {
        katalystParamsOrNew().set("compressor.attack", value)
        fillCompressorDefaults()
    }
}

private fun applyCompressorAttack(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.katalystParams?.get("compressor.attack") }, update = compressorAttackMutation)
    }

    return source._liftOrReinterpretNumericalField(args, compressorAttackMutation)
}

private val compressorReleaseMutation = voiceSetter { raw ->
    val value = raw?.asDoubleOrNull()

    if (value != null) {
        katalystParamsOrNew().set("compressor.release", value)
        fillCompressorDefaults()
    }
}

private fun applyCompressorRelease(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.katalystParams?.get("compressor.release") }, update = compressorReleaseMutation)
    }

    return source._liftOrReinterpretNumericalField(args, compressorReleaseMutation)
}

/**
 * The orbit compressor: threshold, ratio, knee, attack and release.
 *
 * Levels above the threshold are turned down by the ratio, the knee softens the onset, and attack
 * and release set how fast it moves.
 *
 * One compressor per orbit bus, so it hears the whole orbit summed
 * and its settings come from the orbit's owning voice. That is what makes it a drum-bus compressor
 * rather than a per-note one: give a pattern its own orbit to compress it on its own.
 *
 * The call sets every slot: the ones you leave out take the shared defaults, threshold -20 dB,
 * ratio 4:1, knee 6 dB, attack 3 ms and release 100 ms, unless an earlier call already set them. So
 * `compressor(ratio = 8)` is a working compressor at -20 dB, and `compressor.threshold` reads -20
 * after it. Any of the five switches the stage on, because a compressor has no on-knob. Slots apply
 * in order, threshold first, so a mapper on a later slot sees a default an earlier slot of the same
 * call filled in (`compressor(-15, ratio = mul(2))` is ratio 8).
 *
 * Every slot is independent and patternable; an omitted slot keeps its value, a named slot takes
 * a mapper (`compressor(ratio = mul(2))`), and the numeric slots read back as `compressor.threshold`, `compressor.ratio`, `compressor.knee`, `compressor.attack`, `compressor.release`.
 * With no argument at all, the pattern's own values are reinterpreted as `threshold`.
 *
 * ```KlangScript(Playable)
 * s("bd sd hh sd").compressor(-20, 4, 6, 0.003, 0.1)                     // a firm hand on the drum bus
 * ```
 *
 * ```KlangScript(Playable)
 * s("bd sd hh sd").compressor(-20, 4).compressor(ratio = mul("<1 2>"))   // twice the ratio every other bar
 * ```
 *
 * ```KlangScript(Playable)
 * s("bd sd hh sd").compressor(-20, 4).gain(compressor.threshold.mul(-0.02).add(1))       // make-up gain from the threshold
 * ```
 *
 * @param threshold Level in dB where compression starts, such as -20.
 * @param ratio Compression ratio. 4 means 4:1 above the threshold.
 * @param knee Knee width in dB. 0 is hard, 6 and above soft.
 * @param attack Attack in seconds.
 * @param release Release in seconds.
 * @tool SprudelCompressorEditor
 * @tool threshold: SprudelCompressorEditor, SprudelCompressorSequenceEditor
 *
 * @scope orbit
 * @category dynamics
 * @tags compressor, threshold, ratio, knee, attack, release
 */
@KlangScript.Function
fun SprudelPattern.compressor(threshold: PatternLike? = null, ratio: PatternLike? = null, knee: PatternLike? = null, attack: PatternLike? = null, release: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern {
    // A tail-only call must not touch threshold: reinterpret runs only on a fully bare call.
    var p = if (threshold != null || !(ratio != null || knee != null || attack != null || release != null)) {
        applyCompressorThreshold(this, listOfNotNull(threshold).asSprudelDslArgs(callInfo))
    } else {
        this
    }
    if (ratio != null) p = applyCompressorRatio(p, listOf<Any?>(ratio).asSprudelDslArgs(callInfo?.forParam(1)))
    if (knee != null) p = applyCompressorKnee(p, listOf<Any?>(knee).asSprudelDslArgs(callInfo?.forParam(2)))
    if (attack != null) p = applyCompressorAttack(p, listOf<Any?>(attack).asSprudelDslArgs(callInfo?.forParam(3)))
    if (release != null) p = applyCompressorRelease(p, listOf<Any?>(release).asSprudelDslArgs(callInfo?.forParam(4)))
    return p
}

/** Parses this string as a pattern, then applies [compressor]. */
@KlangScript.Function
fun String.compressor(threshold: PatternLike? = null, ratio: PatternLike? = null, knee: PatternLike? = null, attack: PatternLike? = null, release: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).compressor(threshold, ratio, knee, attack, release, callInfo)

/** Chains a [compressor] step onto this [PatternMapperFn]. */
@KlangScript.Function
fun PatternMapperFn.compressor(threshold: PatternLike? = null, ratio: PatternLike? = null, knee: PatternLike? = null, attack: PatternLike? = null, release: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
    this.chain { p -> p.compressor(threshold, ratio, knee, attack, release, callInfo) }

/**
 * The `compressor` object: `compressor(...)` sets the slots, and each numeric slot reads back as a child,
 * `compressor.threshold`, `compressor.ratio`, `compressor.knee`, `compressor.attack`, `compressor.release`.
 *
 * @scope orbit
 * @category dynamics
 * @tags compressor, accessor
 */
@KlangScript.Library("sprudel")
@KlangScript.Object("compressor")
object compressor {

    /** The threshold slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val threshold: FieldAccessor = FieldAccessor { it.katalystParams?.get("compressor.threshold") }

    /** The ratio slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val ratio: FieldAccessor = FieldAccessor { it.katalystParams?.get("compressor.ratio") }

    /** The knee slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val knee: FieldAccessor = FieldAccessor { it.katalystParams?.get("compressor.knee") }

    /** The attack slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val attack: FieldAccessor = FieldAccessor { it.katalystParams?.get("compressor.attack") }

    /** The release slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val release: FieldAccessor = FieldAccessor { it.katalystParams?.get("compressor.release") }

    /**
     * The setter, see [SprudelPattern.compressor].
     *
     * @param threshold Level in dB where compression starts, such as -20.
     * @param ratio Compression ratio. 4 means 4:1 above the threshold.
     * @param knee Knee width in dB. 0 is hard, 6 and above soft.
     * @param attack Attack in seconds.
     * @param release Release in seconds.
     */
    @KlangScript.Invoke
    operator fun invoke(threshold: PatternLike? = null, ratio: PatternLike? = null, knee: PatternLike? = null, attack: PatternLike? = null, release: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
        { p -> p.compressor(threshold, ratio, knee, attack, release, callInfo) }
}

/**
 * `comp`, the short name of [compressor]: the same door, use whichever reads better.
 *
 * ```KlangScript(Playable)
 * s("bd sd hh sd").comp(-20, 4, 6, 0.003, 0.1)
 * ```
 *
 * @scope orbit
 * @category dynamics
 * @tags comp, compressor
 * @tool SprudelCompressorEditor
 * @tool threshold: SprudelCompressorEditor, SprudelCompressorSequenceEditor
 */
@KlangScript.Function
fun SprudelPattern.comp(threshold: PatternLike? = null, ratio: PatternLike? = null, knee: PatternLike? = null, attack: PatternLike? = null, release: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern =
    compressor(threshold, ratio, knee, attack, release, callInfo)

/** Parses this string as a pattern, then applies [comp]. */
@KlangScript.Function
fun String.comp(threshold: PatternLike? = null, ratio: PatternLike? = null, knee: PatternLike? = null, attack: PatternLike? = null, release: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern =
    this.compressor(threshold, ratio, knee, attack, release, callInfo)

/**
 * Alias of [compressor]: the same object under its short name.
 *
 * @scope orbit
 * @category dynamics
 * @tags comp, compressor, accessor
 */
@KlangScript.Constant
val comp: compressor = compressor

/** Chains a [comp] step onto this [PatternMapperFn] (see [SprudelPattern.comp]). */
@KlangScript.Function
fun PatternMapperFn.comp(threshold: PatternLike? = null, ratio: PatternLike? = null, knee: PatternLike? = null, attack: PatternLike? = null, release: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
    this.compressor(threshold, ratio, knee, attack, release, callInfo)
